import React, { useEffect, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { useObservable } from 'react-use-observable';

import { CloseCircleOutlined, QuestionCircleOutlined } from '@ant-design/icons';
import { Alert, Select, Tooltip } from 'antd';

import MaskedInput from 'components/MaskedInput';
import { VERTICAL_URL } from 'settings';
import { numberToBRL } from 'v2/helpers/money';
import eduzzAccountsService from 'v2/services/eduzzAccount';
import { financialStatementService } from 'v2/services/financialStatement/statement';
import * as Yup from 'yup';

import { MIN_TRANSFER, MIN_TRANSFER_INTERNATIONAL } from './contants';
import type { TransferByInvoice } from '../../constants';

type selectOption = {
  label: string;
  value: string;
};

type Props = {
  loading: boolean;
  formIsValid: boolean;
  selectedBank: number;
  accountBankList: selectOption[];
  setSelectedBank: React.Dispatch<React.SetStateAction<number>>;
  valueToTransfer: string | null;
  setValueToTransfer: React.Dispatch<React.SetStateAction<string>>;
  setFormIsValid: (...args: any[]) => any;
  transferByInvoiceObj: TransferByInvoice;
  resetTransferByInvoiceObj: (...args: any[]) => any;
};

function TransferStep({
  loading,
  formIsValid,
  selectedBank,
  accountBankList,
  setSelectedBank,
  valueToTransfer,
  setValueToTransfer,
  setFormIsValid,
  transferByInvoiceObj,
  resetTransferByInvoiceObj
}: Props) {
  const [formMessageError, setFormMessageError] = useState(null);

  const [currentBalance] = useObservable(() => financialStatementService.getCurrentBalance(), []);
  const [user] = useObservable(() => eduzzAccountsService.getUser(), []);

  const balanceFormatted = typeof currentBalance === 'number' ? numberToBRL(currentBalance) : '-';

  const validationMinValue = user?.international_account ? MIN_TRANSFER_INTERNATIONAL : MIN_TRANSFER;
  const validationMinValueFormatted = numberToBRL(validationMinValue);
  const validationMaxValue = currentBalance;
  const validationMaxValueFormatted = balanceFormatted;

  const validateValue = async (value: string) => {
    const transferSchema = Yup.object().shape({
      valueToTransfer: Yup.number()
        .required()
        .min(validationMinValue, `Valor mínimo: ${validationMinValueFormatted}`)
        .max(validationMaxValue, `Valor máximo: ${validationMaxValueFormatted}`)
    });

    try {
      await transferSchema.validate({ valueToTransfer: value });
      setFormIsValid(true);
      setFormMessageError(null);
    } catch (err) {
      setFormIsValid(false);
      setFormMessageError(err.message);
    }
  };

  const handleInputChange = (value: string) => {
    resetTransferByInvoiceObj();
    validateValue(value);
    setValueToTransfer(value);
  };

  const handleInputReset = () => {
    setValueToTransfer('');
    validateValue('0');
  };

  const getTransferByInvoiceMessage = () => {
    const transferByInvoiceFormatted = numberToBRL(transferByInvoiceObj.approx_amount);

    if (transferByInvoiceObj.approx_amount < validationMinValue) {
      return (
        <>
          <div>{`O valor mínimo para saque em nossa plataforma é de ${validationMinValueFormatted} e a soma de faturas com valor mais próximo ao solicitado é de ${transferByInvoiceFormatted}.`}</div>
          <div>Tente novamente com um valor acima disso e dará tudo certo</div>
        </>
      );
    }

    return `A soma das faturas com valor mais próximo ao solicitado é de ${transferByInvoiceFormatted}. Clique em "Transferir valor" para prosseguir com o valor apurado.`;
  };

  useEffect(() => setValueToTransfer(String(currentBalance)), [currentBalance, setValueToTransfer]);

  return (
    <>
      <p className='text-text-heading text-sm leading-[150%] font-normal'>
        Informe o valor que deseja transferir para sua conta bancária.
      </p>

      <div className='text-grey-500 my-3.5 flex items-center justify-between text-base leading-[150%]'>
        <span>Disponível para transferência:</span>
        <strong>{validationMaxValueFormatted}</strong>
      </div>

      <div className='text-grey-500 flex items-center justify-between border-t border-[#ECEFF1] py-3.5 text-base leading-[150%]'>
        <span>
          Valor que deseja transferir:
          <Tooltip
            title='Será debitado o valor de R$ 9,00 do valor transferido devido encargos bancários.'
            placement='top'
          >
            <QuestionCircleOutlined className='ml-1 h-4 w-4' style={{ fontSize: 16 }} />
          </Tooltip>
        </span>
        <div>
          <MaskedInput
            className='max-w-[200px] [&_fieldset]:border-[#b0bec5] [&_input]:h-auto [&_input]:py-1.5 [&_input]:pr-0 [&_input]:pl-1.5 [&_input]:text-right [&>div]:pr-0'
            name='valueToTransfer'
            placeHolder='R$ 0,00'
            mask='money'
            disabled={typeof valueToTransfer === null || loading}
            value={valueToTransfer}
            onChange={handleInputChange}
            error={!formIsValid}
            maskCleanValue
            endAdornment={
              <CloseCircleOutlined
                className='cursor-pointer text-base text-[#B0BEC5]'
                style={{ fontSize: 16 }}
                onClick={handleInputReset}
              />
            }
          />
          {!formIsValid && formMessageError && <div className='mt-1 text-xs text-red-500'>{formMessageError}</div>}
        </div>
      </div>

      {!!transferByInvoiceObj && <Alert type='warning' showIcon message={getTransferByInvoiceMessage()} />}

      <div className='border-t border-[#ECEFF1]'>
        {!!accountBankList && (
          <>
            <label className='text-grey-500 block py-4 text-sm'>Conta bancária</label>
            <Select
              value={selectedBank}
              onChange={value => setSelectedBank(value as number)}
              disabled={loading || !accountBankList}
              className='w-full'
            >
              {accountBankList?.map((option, index) => (
                <Select.Option key={`${option.value}-${index}`} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </>
        )}
        {!accountBankList && (
          <Alert
            type='warning'
            showIcon
            message={
              <>
                Falta pouco! Como você não tem nenhuma conta bancária ativa no momento, <BankAccountsLink />
              </>
            }
          />
        )}
      </div>
    </>
  );
}

function BankAccountsLink() {
  const userHasRemoveNewBankAccountsBeta = eduzzAccountsService.verifyUserHasBetaModule('remove_new_bank_accounts');

  if (userHasRemoveNewBankAccountsBeta) {
    return (
      <RouterLink to='/producer/profile/bankdata'>
        <strong>clique aqui e cadastre agora!</strong>
      </RouterLink>
    );
  }

  return (
    <a href={`${VERTICAL_URL}/bank-accounts/`}>
      <strong>clique aqui e cadastre agora!</strong>
    </a>
  );
}

export default TransferStep;
